import classNames from 'classnames';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { useCallback } from 'react';

import {
  HeaderLogoFragmentFragment,
  HeaderMenuItemFragmentFragment,
  MenuButtonFragmentFragment,
} from '../../../__generated__/graphql';
import MenuButton from '../../../components/Button/MenuButton';
import Caret from '../../../components/icons/Caret';
import Svg from '../../../components/Svg';
import { Heading6, Paragraph, TextLink } from '../../../components/Text';
import { convertHref } from '../../../utils/convertHref';
import { removeTrailingSlash } from '../../../utils/removeTrailingSlash';
import { sanitizeHtml } from '../../../utils/sanitzeHtml';
import { menuButtonFragmentToMenuButtonItems } from '../helpers';

import style from './desktop-nav.module.css';

interface DesktopNavProps {
  className: string;
  headerLogo: HeaderLogoFragmentFragment | undefined;
  menuButton: MenuButtonFragmentFragment;
  menuItems: HeaderMenuItemFragmentFragment[];
}

const headerButtonId = 'header-login';

// A couple of items use dangerouslySetInnerHTML. The API returns HTML entities
// that need to be converted to HTML.
const DesktopNav = (props: DesktopNavProps) => {
  const router = useRouter();

  const menuButtonItems = menuButtonFragmentToMenuButtonItems(props.menuButton);

  const [openMenuId, setOpenMenuId] = React.useState<null | string>(null);

  const handleSetOpenMenuId: React.MouseEventHandler<HTMLAnchorElement | HTMLButtonElement> = useCallback(evt => {
    const menuId = evt.currentTarget.getAttribute('data-menu-id');

    if (menuId) {
      setOpenMenuId(menuId === openMenuId ? null : menuId);
    }
    else {
      setOpenMenuId(null);
    }
  }, [openMenuId]);

  React.useEffect(() => {
    const handleClickOutsideMenu = (evt: MouseEvent) => {
      if (openMenuId) {
        // eslint-disable-next-line @stylistic/max-len
        const menuContainer = document.getElementById(`menu-${openMenuId}`)?.getElementsByClassName(style.menuContainer)[0];
        const headerLoginButton = document.getElementById(headerButtonId);

        if (((evt.target as Element | null)?.id !== menuContainer?.id)
          || ((evt.target as Element | null)?.id === headerLoginButton?.id)) {
          setOpenMenuId(null);
        }
      }
    };

    document.addEventListener('click', handleClickOutsideMenu);

    return () => {
      document.removeEventListener('click', handleClickOutsideMenu);
    };
  }, [openMenuId]);

  return (
    <div
      className={classNames(style.desktopNav, props.className)}
      id="desktop-nav"
    >
      <div className={style.fullWidthHeaderContainer}>
        <div className={style.headerContainer}>
          {props.headerLogo
            ? (
              <Link
                aria-label="Navigate to Home"
                className={style.logoLink}
                href="/"
              >
                <Svg
                  image={{
                    node: {
                      ...props.headerLogo,
                    },
                  }}
                  priority
                />
              </Link>
            )
            : <div />}

          <div className={style.navContainer}>
            <nav className={style.nav}>
              <ul>
                {props.menuItems.map(item => (
                  <li key={item.id}>
                    <button
                      className={openMenuId === item.id ? style.navButtonActive : undefined}
                      data-menu-id={item.id}
                      onClick={handleSetOpenMenuId}
                    >
                      {item.label}

                      <div
                        className={classNames(
                          style.navIconContainer,
                          openMenuId === item.id ? style.navCaretActive : '',
                        )}
                      >
                        <Caret direction="down" />
                      </div>
                    </button>

                    <div className={style.navButtonUnderline} />
                  </li>
                ))}
              </ul>
            </nav>
          </div>

          <MenuButton
            id={headerButtonId}
            menuItems={menuButtonItems}
          >
            Log In
          </MenuButton>
        </div>
      </div>

      <div className={style.mainContentWrapper}>
        {props.menuItems.map(item => {
          const navDetails = item.navDetails;

          const isMainContentLinkActive = navDetails?.link
            ? removeTrailingSlash(convertHref(navDetails.link.url)) === removeTrailingSlash(router.asPath)
            : false;

          const segments = item.navSegments?.segments;

          const menuItemCount = segments?.length ?? 0;

          return (
            <div
              className={classNames(
                style.menu,
                openMenuId === item.id ? style.menuOpen : '',
              )}
              id={`menu-${item.id}`}
              key={`menu-${item.id}`}
            >
              <div className={style.menuContainer}>
                <div className={style.menuLeftContainer}>
                  <Paragraph fontSize={14}>
                    {navDetails?.description}
                  </Paragraph>

                  <TextLink
                    fontSize={14}
                    fontWeight={700}
                    linkFragment={navDetails?.link}
                    onClick={isMainContentLinkActive ? handleSetOpenMenuId : undefined}
                    prefetch={openMenuId === item.id}
                  />
                </div>

                <div
                  className={classNames(
                    style.menuLinks,
                    {
                      [style.menuLinkColumnCount3]: menuItemCount > 12,
                    },
                  )}
                >
                  {segments?.map(section => {
                    if (section) {
                      return (
                        <div
                          className={style.menuLinksSection}
                          key={section.title}
                        >
                          <Heading6
                            // eslint-disable-next-line @typescript-eslint/naming-convention
                            dangerouslySetInnerHTML={{ __html: sanitizeHtml(section.title ?? '') }}
                            uppercase
                          />

                          <ul>
                            {section.links?.map((link, index) => {
                              const isActive = removeTrailingSlash(convertHref(link?.link?.url))
                                === removeTrailingSlash(router.asPath);

                              return (
                                <li key={link?.link?.title ?? `link-${index.toString()}`}>
                                  <TextLink
                                    // eslint-disable-next-line @typescript-eslint/naming-convention
                                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(link?.link?.title ?? '') }}
                                    fontSize={14}
                                    linkFragment={link?.link}
                                    onClick={isActive ? handleSetOpenMenuId : undefined}
                                    prefetch={openMenuId === item.id}
                                  />
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      );
                    }
                  })}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DesktopNav;
